import Vue from 'vue'
import Vuex from 'vuex'

import { db, firebase } from '@/fb'

Vue.use(Vuex)

const SITE_TEXTS_REF = () => db.collection('site').doc('texts')

function updateSiteTextsCache(values) {
  const CACHE_KEY = 'siteTexts_v1'
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ values: values || {}, fetchedAt: Date.now() }))
    }
  } catch {
    // ignore
  }
}

export default new Vuex.Store({
  state: {
    isLogin: false,

    docs: [],
    docsLoaded: false,
    activeDoc: '',

    reviews: [],
    reviewsLoaded: false,

    // Key-based texts
    // values: { [key: string]: string }
    siteTexts: {},
    siteTextsLoaded: false,

    // defaults registered by <SiteText> from slots (used for initial seeding UI)
    siteTextDefaults: {},
  },
  mutations: {
    CHANGE_LOGIN(state, value) {
      state.isLogin = value !== undefined ? value : !state.isLogin
    },

    SET_DOCS(state, value) {
      state.docs = value
      state.docsLoaded = true
    },
    PUSH_DOCS(state, { value, method = 'push' }) {
      state.docs[method](value)
    },
    SET_ACTIVE_DOC(state, value) {
      state.activeDoc = value
    },
    REMOVE_DOC(state, index) {
      state.docs.splice(index, 1)
    },
    SAVE_DOC(state, { doc, index }) {
      state.docs.splice(index, 1, doc)
    },
    UPDATE_DOC_FIELD(state, { id, field, value }) {
      const ind = state.docs.findIndex((d) => d.id === id)
      if (ind >= 0) state.docs[ind] = { ...state.docs[ind], [field]: value }
    },
    UPDATE_DOC_ARRAY_ITEM_FIELD(state, { id, field, index, subfield, value }) {
      const ind = state.docs.findIndex((d) => d.id === id)
      if (ind < 0) return
      const doc = state.docs[ind]
      const arr = Array.isArray(doc[field]) ? [...doc[field]] : []
      if (!arr[index]) return
      arr[index] = { ...arr[index], [subfield]: value }
      state.docs[ind] = { ...doc, [field]: arr }
    },

    SET_REVIEWS(state, value) {
      state.reviews = value
      state.reviewsLoaded = true
    },
    PUSH_REVIEW(state, { value, method = 'push' }) {
      state.reviews[method](value)
    },
    REMOVE_REVIEW(state, index) {
      state.reviews.splice(index, 1)
    },
    UPDATE_REVIEW_FIELD(state, { id, field, value }) {
      const ind = state.reviews.findIndex((r) => r.id === id)
      if (ind >= 0) state.reviews[ind] = { ...state.reviews[ind], [field]: value }
    },

    SET_SITE_TEXTS(state, value) {
      state.siteTexts = value || {}
      state.siteTextsLoaded = true
    },
    SET_SITE_TEXT_VALUE(state, { key, value }) {
      state.siteTexts = { ...state.siteTexts, [key]: value }
    },
    DELETE_SITE_TEXT_VALUE(state, { key }) {
      const copy = { ...state.siteTexts }
      delete copy[key]
      state.siteTexts = copy
    },
    REGISTER_SITE_TEXT_DEFAULT(state, { key, value }) {
      if (!key || !value) return
      if (state.siteTextDefaults[key]) return
      state.siteTextDefaults = { ...state.siteTextDefaults, [key]: value }
    },
  },
  actions: {
    TOGGLE_LOGIN({ commit }, value) {
      commit('CHANGE_LOGIN', value)
    },

    async GET_DOCS({ commit, state }) {
      if (state.docsLoaded && state.docs.length) return
      const snapshot = await db.collection('docs').get()
      const list = snapshot.docs.map((d) => ({ ...d.data(), id: d.id, uploaded: true }))
      commit('SET_DOCS', list)
    },

    ADD_NEW_DOC({ commit, getters }, { doc, method = 'push' } = {}) {
      commit('PUSH_DOCS', {
        value:
          doc || {
            tag: '',
            sectionName: '',
            docs: [],
            id: new Date().getTime(),
            uploaded: false,
          },
        method,
      })
      return getters.LAST_DOC
    },
    REMOVE_DOC({ commit }, index) {
      commit('REMOVE_DOC', index)
    },
    SAVE_DOC({ commit }, { doc, index }) {
      commit('SAVE_DOC', { doc, index })
    },
    GET_DOC({ state }, index) {
      return state.docs[index]
    },

    async GET_REVIEWS({ commit, state }) {
      if (state.reviewsLoaded && state.reviews.length) return
      const snapshot = await db.collection('reviews').orderBy('published', 'desc').get()
      const list = snapshot.docs.map((d) => {
        const chunk = d.data()
        return {
          ...chunk,
          text: String(chunk.text || '').replace('\\n', '\n'),
          published: chunk.published?.toDate ? chunk.published.toDate() : chunk.published,
          id: d.id,
        }
      })
      commit('SET_REVIEWS', list)
    },

    async GET_SITE_TEXTS({ commit, state }, { force = false } = {}) {
      if (state.siteTextsLoaded && !force) return

      // Fast path: short-lived local cache to reduce Firestore reads
      const CACHE_KEY = 'siteTexts_v1'
      const CACHE_TTL_MS = 5 * 60 * 1000
      try {
        const cachedRaw = typeof localStorage !== 'undefined' && localStorage.getItem(CACHE_KEY)
        if (cachedRaw) {
          const cached = JSON.parse(cachedRaw)
          if (cached && cached.values && typeof cached.fetchedAt === 'number') {
            commit('SET_SITE_TEXTS', cached.values)
            if (Date.now() - cached.fetchedAt < CACHE_TTL_MS) return
          }
        }
      } catch {
        // ignore
      }

      const ref = SITE_TEXTS_REF()
      const snap = await ref.get()

      if (!snap.exists) {
        await ref.set({
          values: {},
          updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        })
        commit('SET_SITE_TEXTS', {})
        try {
          if (typeof localStorage !== 'undefined') {
            localStorage.setItem(CACHE_KEY, JSON.stringify({ values: {}, fetchedAt: Date.now() }))
          }
        } catch {
          // ignore
        }
        return
      }

      const data = snap.data() || {}
      const values = data.values || {}
      commit('SET_SITE_TEXTS', values)
      try {
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem(CACHE_KEY, JSON.stringify({ values, fetchedAt: Date.now() }))
        }
      } catch {
        // ignore
      }
    },

    REGISTER_SITE_TEXT_DEFAULT({ commit }, { key, value }) {
      commit('REGISTER_SITE_TEXT_DEFAULT', { key, value })
    },

    async SET_SITE_TEXT({ commit, state }, { key, value }) {
      if (!key) throw new Error('Missing key')
      await SITE_TEXTS_REF().set(
        {
          values: { [key]: value },
          updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        },
        { merge: true },
      )
      commit('SET_SITE_TEXT_VALUE', { key, value })
      updateSiteTextsCache({ ...(state.siteTexts || {}), [key]: value })
    },

    async SET_SITE_TEXTS_BULK({ commit, state }, { values }) {
      const entries = values && typeof values === 'object' ? Object.entries(values) : []
      if (!entries.length) return
      await SITE_TEXTS_REF().set(
        {
          values,
          updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        },
        { merge: true },
      )
      entries.forEach(([key, value]) => {
        if (typeof key === 'string' && typeof value === 'string') {
          commit('SET_SITE_TEXT_VALUE', { key, value })
        }
      })
      updateSiteTextsCache({ ...(state.siteTexts || {}), ...(values || {}) })
    },

    async DELETE_SITE_TEXT({ commit, state }, { key }) {
      if (!key) throw new Error('Missing key')
      await SITE_TEXTS_REF().set(
        {
          values: { [key]: firebase.firestore.FieldValue.delete() },
          updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        },
        { merge: true },
      )
      commit('DELETE_SITE_TEXT_VALUE', { key })
      const next = { ...(state.siteTexts || {}) }
      delete next[key]
      updateSiteTextsCache(next)
    },

    async DELETE_SITE_TEXTS_BULK({ commit, state }, { keys }) {
      const list = Array.isArray(keys) ? keys.filter(Boolean) : []
      if (!list.length) return
      const del = {}
      list.forEach((k) => {
        del[k] = firebase.firestore.FieldValue.delete()
      })
      await SITE_TEXTS_REF().set(
        {
          values: del,
          updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        },
        { merge: true },
      )
      list.forEach((key) => commit('DELETE_SITE_TEXT_VALUE', { key }))
      const next = { ...(state.siteTexts || {}) }
      list.forEach((k) => delete next[k])
      updateSiteTextsCache(next)
    },

    async SEED_SITE_TEXTS({ state }) {
      const defaults = state.siteTextDefaults || {}
      if (!defaults || !Object.keys(defaults).length) return
      const current = state.siteTexts || {}
      const merged = { ...defaults, ...current } // keep overrides
      await SITE_TEXTS_REF().set(
        {
          values: merged,
          updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        },
        { merge: true },
      )
    },
  },
  getters: {
    LOGIN_STATE: (state) => state.isLogin,

    DOCS_STATE: (state) => state.docs,
    ACTIVE_DOC_STATE: (state) => state.activeDoc,
    ACTIVE_DOC_INDEX: (state) => state.docs.findIndex((x) => x.tag === state.activeDoc),
    LAST_DOC: (state) => state.docs[state.docs.length - 1],

    REVIEWS_STATE: (state) => state.reviews,

    SITE_TEXTS_STATE: (state) => state.siteTexts,
    SITE_TEXT_DEFAULTS_STATE: (state) => state.siteTextDefaults,
    SITE_TEXT_VALUE: (state) => (key) => {
      const values = state.siteTexts || {}
      return values[key]
    },
  },
})
